import { useAuth0 } from '@auth0/auth0-react';
import { Button, Spacer, Switch } from '@odekoteam/doppio';
import { useState } from 'react';
import { useUpdateLocationCustomerPickup } from 'src/components/LocationsTable/useUpdateLocationCustomerPickup';
import { GetLocation } from 'src/graphql/types/delivery';
import { useAppSelector } from 'src/hooks/store';
import { modalSelector } from 'src/state/selectors/modal';
import { hasAdminRole } from 'src/utils/authRoles';
import { Flex } from 'theme-ui';
import CancelButton from '../CancelButton';
import {
  CUSTOMER_PICKUP_ON_SWEEP,
  CUSTOMER_PICKUP_OFF_BODY,
} from '../CustomerPickupToggleModal';
import ExpandableLocationsList from './ExpandableLocationsList';

// The row dialog's ON copy, said about a set. Written out rather than derived
// from the singular by substitution, which would quietly ship singular copy the
// day that sentence is reworded. The OFF body and the sweep sentence already
// read for any number of locations, so they are reused as they are.
export const CUSTOMER_PICKUP_BULK_ON_BODY =
  'New orders for these locations are placed in Customer Pick Ups instead of the Lobby.';
export const CUSTOMER_PICKUP_BULK_OFF_BODY = CUSTOMER_PICKUP_OFF_BODY;

export const NOTHING_TO_CHANGE = 'Nothing to change';

const CustomerPickupTab = (): JSX.Element | null => {
  const {
    variables: { locations, metrobiContextId },
  } = useAppSelector(modalSelector);
  const { user } = useAuth0();
  // updateLocationsCustomerPickup is declared authorized_roles: %w[admin], so
  // bp-admin (which useMetrobiAdmin admits) would get a server rejection.
  const isAdmin = hasAdminRole(user as Record<string, unknown> | undefined);
  const { submit, isSubmitting } = useUpdateLocationCustomerPickup();
  // Off unless the operator chooses otherwise, unlike every other tab here.
  // Turning pickup on reroutes every future order for a location and queues a
  // sweep per location, so it is not something to arrive at by pressing Update.
  const [customerPickup, setCustomerPickup] = useState(false);

  const selected = Array.from(
    new Map<string, GetLocation>(
      (locations as GetLocation[]).map((location) => [location.id, location]),
    ).values(),
  );
  // Boolean() because the field is nullable: a null must read as Off, or
  // turning Off would count it as a change. Matches the row switch.
  const changing = selected.filter(
    (location) => Boolean(location.customerPickup) !== customerPickup,
  );

  const handleAction = async (): Promise<void> => {
    if (!changing.length) return;
    await submit({
      // Everything selected, not just the rows this snapshot thinks will
      // change: the server re-derives that itself from current data, and a row
      // another operator moved since this page loaded would otherwise be
      // silently skipped while the toast reported success.
      locations: selected.map((location) => ({
        id: location.id,
        businessName: location.businessName,
        // What this row reads as now, so the outcome can be reported per row
        // rather than as one count taken before the write.
        current: Boolean(location.customerPickup),
      })),
      customerPickup,
      contextId: metrobiContextId,
      returnFocusId: 'update-locations-button',
    });
  };

  const label = customerPickup ? 'ON' : 'OFF';

  return (
    <div data-test-id="customer-pickup-tab">
      <Flex sx={{ alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <span sx={{ variant: 'text.paragraphMdDefault' }}>
          Update customer pickup to:
        </span>
        <Switch
          id="customer-pickup-bulk"
          accessibilityLabel="Customer pickup for selected locations"
          isChecked={customerPickup}
          isDisabled={isSubmitting || !isAdmin}
          onCheckedChange={setCustomerPickup}
        />
        <span sx={{ variant: 'text.paragraphLgDefault' }}>{label}</span>
      </Flex>
      <p sx={{ variant: 'text.paragraphMdDefault' }}>
        {selected.length} selected, {changing.length} will change.
      </p>
      <p sx={{ variant: 'text.paragraphMdDefault' }}>
        {customerPickup
          ? CUSTOMER_PICKUP_BULK_ON_BODY
          : CUSTOMER_PICKUP_BULK_OFF_BODY}
      </p>
      {customerPickup && (
        <p sx={{ variant: 'text.paragraphMdDefault' }}>
          {CUSTOMER_PICKUP_ON_SWEEP}
        </p>
      )}
      <ExpandableLocationsList locations={selected} />
      {!isAdmin && (
        <p sx={{ variant: 'text.paragraphSmDefault' }}>
          Admin access required to edit
        </p>
      )}
      {isAdmin && !changing.length && (
        <p sx={{ variant: 'text.paragraphSmDefault' }}>{NOTHING_TO_CHANGE}</p>
      )}
      {isSubmitting && <p role="status">Saving customer pickup…</p>}
      <Flex sx={{ justifyContent: 'center', marginTop: '24px' }}>
        <Button
          id="update-customer-pickup"
          variant="secondary"
          onPress={handleAction}
          isDisabled={isSubmitting || !isAdmin || !changing.length}
        >
          Update
        </Button>
        <Spacer width="$1" />
        <CancelButton isDisabled={isSubmitting} />
      </Flex>
    </div>
  );
};
export default CustomerPickupTab;
